import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import WorkflowSidebar from './components/WorkflowSidebar';
import MethodCard from './components/MethodCard';
import CancelButton from './components/CancelButton';
import apiClient from '../../api/client';
import SaveResultsWizard from '../../components/chemistry/SaveResultsWizard';
import StructureImage from '../../components/chemistry/StructureImage';
import { useWorkbenchContext } from '../../hooks/useWorkbenchContext';
import WorkbenchContextBar from '../../components/workbench/WorkbenchContextBar';
import ToolSwitcher from '../../components/workbench/ToolSwitcher';
import { useToolStep } from '../../hooks/useWorkbenchContext';

const STEPS = [
  { name: 'Input' },
  { name: 'Configure' },
  { name: 'Calculate' },
  { name: 'Results' },
];

interface PaDELResult {
  smiles: string;
  csv_text: string;
  rows: number;
  cols: number;
}

function parseCsv(csv: string): { headers: string[]; values: string[] } {
  const lines = csv.trim().split('\n');
  if (lines.length < 2) return { headers: [], values: [] };
  const headers = lines[0].split(',').map((h) => h.replace(/"/g, ''));
  const values = lines[1].split(',').map((v) => v.replace(/"/g, ''));
  return { headers, values };
}

const WorkbenchPadel: React.FC = () => {
  const { smiles: ctxSmiles, moleculeId: ctxMoleculeId, fileId: ctxFileId, projectId: ctxProjectId } = useWorkbenchContext();
  const [step, setStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [inputText, setInputText] = useState(ctxSmiles || '');
  const [selectedExample, setSelectedExample] = useState('');
  const [filterText, setFilterText] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<PaDELResult | null>(null);
  const [error, setError] = useState('');
  const abortRef = useRef<AbortController | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const { bailOutText, setHasSaved } = useToolStep();

  const runCalculation = async () => {
    setIsRunning(true);
    setError('');
    setResult(null);
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const resp = await apiClient.post('/v2/descriptors/padel', {
        smiles: inputText.trim(),
      }, { signal: controller.signal });
      setResult(resp.data);
      setCompletedSteps((prev) => Array.from(new Set([...prev, 2])));
      setStep(3);
    } catch (err: any) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') setError('Calculation cancelled by user');
      else setError(err.response?.data?.detail || err.message || 'Calculation failed');
    } finally {
      setIsRunning(false);
      abortRef.current = null;
    }
  };

  const parsed = result ? parseCsv(result.csv_text) : null;
  const filteredIndices = parsed
    ? parsed.headers.reduce<number[]>((acc, h, i) => {
        if (i === 0) return acc; // skip Name column
        if (!filterText.trim() || h.toLowerCase().includes(filterText.toLowerCase())) acc.push(i);
        return acc;
      }, [])
    : [];

  return (
    <>
    <WorkbenchContextBar />
    <div style={{ display: 'flex', height: 'calc(100vh - 84px)', background: 'var(--content-bg)' }}>
      <WorkflowSidebar steps={STEPS} currentStep={step} completedSteps={completedSteps} onStepClick={setStep} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={statusBarStyle}>
          <Link to="/hubs" style={{ color: '#f59e0b', textDecoration: 'none' }}>Toolbox</Link>
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <ToolSwitcher currentToolId="padel" />
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <span>Engine: PaDEL + Molecular Hub</span>
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <span>Step {step + 1}/{STEPS.length}</span>
          <div style={{ flex: 1 }} />
          <CancelButton onCancel={() => abortRef.current?.abort()} isRunning={isRunning} label="Stop Calculation" />
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 24, display: 'flex', gap: 20 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {step === 0 && (
              <div>
                <h2 style={h2Style}>Enter Molecule</h2>
                <p style={descStyle}>Molecule input for PaDEL descriptor calculation (1400+ 2D descriptors).</p>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                  {[
                    { name: 'Aspirin', smi: 'CC(=O)Oc1ccccc1C(=O)O' },
                    { name: 'Ibuprofen', smi: 'CC(C)Cc1ccc(C(C)C(=O)O)cc1' },
                    { name: 'Caffeine', smi: 'Cn1c(=O)c2c(ncn2C)n(C)c1=O' },
                  ].map((ex) => (
                    <button key={ex.smi} onClick={() => { setInputText(ex.smi); setSelectedExample(ex.name); }}
                      style={{ ...btnStyle, ...(inputText === ex.smi ? { background: 'rgba(245,158,11,0.15)', borderColor: '#f59e0b', color: '#f59e0b' } : {}) }}>
                      {ex.name} <StructureImage smiles={ex.smi} size={24} />
                    </button>
                  ))}
                </div>
                <input type="text" value={inputText} onChange={(e) => { setInputText(e.target.value); setSelectedExample(''); }}
                  placeholder="Enter molecule notation..." style={{ ...textareaStyle, height: 40 }} />
                {selectedExample && inputText && (
                  <div style={{ marginTop: 4, color: 'var(--text-secondary)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>Selected: {selectedExample} <StructureImage smiles={inputText} size={24} /></div>
                )}
                <button onClick={() => { if (inputText.trim()) { setCompletedSteps((prev) => Array.from(new Set([...prev, 0]))); setStep(1); } }}
                  disabled={!inputText.trim()} style={{ ...primaryBtnStyle, marginTop: 12, opacity: inputText.trim() ? 1 : 0.5 }}>Continue</button>
              </div>
            )}

            {step === 1 && (
              <div>
                <h2 style={h2Style}>Descriptor Configuration</h2>
                <p style={descStyle}>
                  PaDEL computes all available 2D descriptors and fingerprints for the given molecule. The backend handles SMILES-to-SDF conversion and 3D embedding automatically.
                </p>
                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, padding: 16, maxWidth: 500 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>Molecule</span>
                      <StructureImage smiles={inputText} size={32} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>Descriptor Types</span>
                      <span style={{ color: 'var(--text-primary)', fontSize: 13 }}>2D + Fingerprints</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>Expected Output</span>
                      <span style={{ color: 'var(--text-primary)', fontSize: 13 }}>~1400 descriptors</span>
                    </div>
                  </div>
                </div>
                <button onClick={() => { setCompletedSteps((prev) => Array.from(new Set([...prev, 1]))); setStep(2); }}
                  style={{ ...primaryBtnStyle, marginTop: 20 }}>Continue</button>
              </div>
            )}

            {step === 2 && (
              <div>
                <h2 style={h2Style}>Run PaDEL Calculation</h2>
                <div style={{ background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.3)', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
                  <div style={{ color: '#a78bfa', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
                    PaDEL calculations may take 30-60 seconds
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                    The molecule is converted to 3D, optimised with MMFF94, then sent to the PaDEL engine.
                  </div>
                </div>
                <p style={descStyle}>
                  Molecule: <StructureImage smiles={inputText} size={32} />
                </p>
                {!isRunning && !result && (
                  <button onClick={runCalculation} style={{ ...primaryBtnStyle, fontSize: 15, padding: '12px 24px' }}>
                    Calculate Descriptors
                  </button>
                )}
                {isRunning && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={spinnerStyle} />
                    <span style={{ color: '#f59e0b', fontSize: 14 }}>Computing PaDEL descriptors... this may take up to a minute</span>
                  </div>
                )}
                {error && <div style={errorStyle}>{error}</div>}
              </div>
            )}

            {step === 3 && result && parsed && (
              <div>
                <h2 style={h2Style}>PaDEL Descriptors</h2>
                <p style={descStyle}>
                  {result.cols} descriptors calculated for <StructureImage smiles={result.smiles} size={32} />
                </p>

                <div style={{ marginBottom: 12 }}>
                  <input type="text" value={filterText} onChange={(e) => setFilterText(e.target.value)}
                    placeholder="Filter descriptors by name..."
                    style={{ ...textareaStyle, height: 36, maxWidth: 400, fontSize: 13 }} />
                </div>

                <div style={{ maxHeight: 500, overflow: 'auto', border: '1px solid var(--card-border)', borderRadius: 10, marginBottom: 16 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ position: 'sticky', top: 0, background: 'var(--hover-bg)', zIndex: 1 }}>
                        <th style={thStyle}>Descriptor</th>
                        <th style={thStyle}>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredIndices.map((i) => (
                        <tr key={i} style={{ borderBottom: '1px solid var(--card-border)' }}>
                          <td style={tdStyle}>{parsed.headers[i]}</td>
                          <td style={{ ...tdStyle, fontFamily: 'monospace', color: '#f59e0b' }}>{parsed.values[i]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
                  Showing {filteredIndices.length} of {result.cols} descriptors
                </div>

                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => {
                    const blob = new Blob([result.csv_text], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a'); a.href = url; a.download = 'padel_descriptors.csv'; a.click(); URL.revokeObjectURL(url);
                  }} style={primaryBtnStyle}>Download CSV</button>
                  <button onClick={() => {
                    const obj: Record<string, string> = {};
                    parsed.headers.forEach((h, i) => { if (i > 0) obj[h] = parsed.values[i]; });
                    const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a'); a.href = url; a.download = 'padel_descriptors.json'; a.click(); URL.revokeObjectURL(url);
                  }} style={btnStyle}>Download JSON</button>
                  <button onClick={() => navigator.clipboard.writeText(result.csv_text)} style={btnStyle}>Copy CSV</button>
                  <button onClick={() => setSaveOpen(true)} style={btnStyle}>Save to Project</button>
                </div>
                <SaveResultsWizard isOpen={saveOpen} onClose={() => setSaveOpen(false)} rawData={result} toolName="PaDEL" toolKey="padel" smiles={ctxSmiles || inputText} moleculeId={ctxMoleculeId} fileId={ctxFileId} projectId={ctxProjectId} />
              </div>
            )}
          </div>

          <div style={{ width: 320, flexShrink: 0 }}>
            <MethodCard engine="PaDEL" title="PaDEL Molecular Descriptors"
              description="Computes 1400+ 2D molecular descriptors and fingerprints using the PaDEL-Descriptor engine. Descriptors include topological, constitutional, electronic, and geometric features widely used in QSAR modelling."
              parameters={[
                { name: '2D', desc: 'Constitutional, topological, electronic descriptors' },
                { name: 'Fingerprints', desc: 'CDK, MACCS, PubChem, Substructure, AtomPairs2D' },
                { name: 'Output', desc: 'CSV with descriptor names and values' },
              ]}
              reference="Yap (2011) J Comput Chem 32:1466-1474"
              referenceUrl="https://doi.org/10.1002/jcc.21707"
              source="PaDEL-Descriptor via Molecular Hub" />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

const statusBarStyle: React.CSSProperties = { padding: '8px 20px', background: 'var(--hover-bg)', borderBottom: '1px solid var(--card-border)', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 8 };
const h2Style: React.CSSProperties = { color: '#f5f5f5', fontSize: 18, fontWeight: 600, marginBottom: 12, marginTop: 0 };
const descStyle: React.CSSProperties = { color: 'var(--text-muted)', fontSize: 13, marginBottom: 12 };
const btnStyle: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer', fontWeight: 500 };
const primaryBtnStyle: React.CSSProperties = { ...btnStyle, background: '#f59e0b', color: 'var(--content-bg)', border: '1px solid #f59e0b', fontWeight: 600 };
const textareaStyle: React.CSSProperties = { width: '100%', padding: '10px 14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, color: 'var(--text-primary)', fontSize: 14, fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box' };
const errorStyle: React.CSSProperties = { marginTop: 10, padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#fb7185', fontSize: 13 };
const spinnerStyle: React.CSSProperties = { width: 20, height: 20, border: '3px solid var(--card-border)', borderTopColor: '#f59e0b', borderRadius: '50%', animation: 'spin 0.8s linear infinite' };
const thStyle: React.CSSProperties = { textAlign: 'left', padding: '8px 12px', color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--card-border)' };
const tdStyle: React.CSSProperties = { padding: '6px 12px', color: 'var(--text-primary)' };

export default WorkbenchPadel;
